import { useContext, useEffect, useState, useMemo } from "react";
import { AudioContext } from "../services/AudioContext";
import { RadioContext } from "../services/RadioProvider";
import useSyncedTrack from "../services/useSyncedTrack";

function parseDuration(durationStr) {
  if (!durationStr) return 0;
  const parts = durationStr.split(":").map(Number);
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return 0;
}

function formatTimestamp(msOrDate) {
  if (!msOrDate) return "--:--:--";
  const date = typeof msOrDate === "number" ? new Date(msOrDate) : new Date(msOrDate);
  return date.toLocaleTimeString("en-GB", { hour12: false });
}

export default function usePlayerUtils() {
  const { nowPlaying } = useContext(RadioContext);
  const { playbackState, audioTimeMs, setCurrentTimeSec, detachHls, setPlaybackState } = useContext(AudioContext);
  const { manualTrack, setManualTrack, isSeeking, setIsSeeking, dataArrayRef } = useContext(AudioContext);
  const { playbackTimeMs, currentLiveMs, bufferStartMs, progressBarPercent, timeShiftOffsetSec, setTimeShiftOffsetSec } = useContext(AudioContext);
  const audioContext = useContext(AudioContext);
  const audioRef = audioContext?.audioRef ?? null;

  const [initialTrack, setInitialTrack] = useState(null);

  useEffect(() => {
    const firstTrack = nowPlaying?.history?.[0];
    if (firstTrack) {
      setInitialTrack(firstTrack);
    }
  }, [nowPlaying]);

  const syncedTrack = useSyncedTrack(nowPlaying, audioTimeMs, playbackState);
  const currentTrack = isSeeking && manualTrack ? manualTrack : syncedTrack || initialTrack;

  const durationPercent = () => {
    if (playbackState === "stopped" || audioTimeMs === 0) return 0;
    if (!currentTrack?.startedAt) return 0;

    const startedAtMs = new Date(currentTrack?.startedAt).getTime();
    let progress = currentTrack?.duration !== ""
      ? (audioTimeMs - startedAtMs) / (parseDuration(currentTrack?.duration) * 1000)
      : 0;

    if (progress < 0) progress = 0;
    if (progress >= 0.999) progress = 0.999;
    return progress;
  };

  const remainingTime = () => {
    if (playbackState === "stopped" || audioTimeMs === 0) return "";
    if (!currentTrack?.startedAt || !currentTrack?.duration) return "";

    const startedAtMs = new Date(currentTrack.startedAt).getTime();
    const durationSeconds = parseDuration(currentTrack.duration);

    if (!durationSeconds || durationSeconds <= 0) return "";

    const elapsedSeconds = (audioTimeMs - startedAtMs) / 1000;

    const remainingSeconds = Math.max(
      0,
      Math.ceil(durationSeconds - elapsedSeconds)
    );

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };


  const value = useMemo(() => ({
    audioRef,
    detachHls,
    setPlaybackState,
    nowPlaying,
    playbackState,
    audioTimeMs,
    currentLiveMs,
    bufferStartMs,
    currentTrack,
    manualTrack,
    setManualTrack,
    isSeeking,
    setIsSeeking,
    initialTrack,
    setInitialTrack,
    setCurrentTimeSec,
    playbackTimeMs,
    progressBarPercent,
    timeShiftOffsetSec,
    durationPercent,
    remainingTime,
    parseDuration,
    formatTimestamp,
    setTimeShiftOffsetSec,
    dataArrayRef,
  }), [
    playbackState,
    audioTimeMs,
    currentLiveMs,
    bufferStartMs,
    manualTrack,
    isSeeking,
    playbackTimeMs,
    progressBarPercent,
    timeShiftOffsetSec,
    currentTrack,
    initialTrack,
  ]);

  return value;
}
